import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDailyProcessRun } from './hooks/useDailyProcessRun';
import AppDropdown from '../../../components/AppDropdown';

export default function DailyProcessRunScreen() {
  const { state, actions } = useDailyProcessRun();
  const navigate = useNavigate();
  const {
    company,
    branch,
    department,
    employee,
    fromDate,
    toDate,
    isProcessing,
    companyOptions,
    branchOptions,
    departmentOptions,
    employeeOptions,
  } = state;

  const formatDateDisplay = (d) => {
    if (!d) return '';
    return d.split('-').reverse().join('/');
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Daily Process From {formatDateDisplay(fromDate)} to {formatDateDisplay(toDate)}
          </h1>
        </div>

        <Link
          to="/payroll/daily-process"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout matching enterprise ERP standards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card (lg:col-span-8) */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">
              Daily Process Parameters
            </h2>
          </div>

          {/* Form Body */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {/* Company */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Company
                </label>
                <AppDropdown
                  options={companyOptions}
                  value={company}
                  onChange={actions.setCompany}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Branch */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Branch
                </label>
                <AppDropdown
                  options={branchOptions}
                  value={branch}
                  onChange={actions.setBranch}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Department */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Department
                </label>
                <AppDropdown
                  options={departmentOptions}
                  value={department}
                  onChange={actions.setDepartment}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Employee */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Employee
                </label>
                <AppDropdown
                  options={employeeOptions}
                  value={employee}
                  onChange={actions.setEmployee}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* From Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  From Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => actions.setFromDate(e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* To Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  To Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => actions.setToDate(e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isProcessing}
              onClick={actions.handleProcess}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">
                {isProcessing ? 'hourglass_top' : 'published_with_changes'}
              </span>
              <span>{isProcessing ? 'Processing...' : 'Run Process'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/payroll/daily-process')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context Panel (lg:col-span-4) */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS &amp; PIPELINE:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Daily attendance processes raw biometric swipes, approved remote GPS attendances, and manual punches against assigned shifts, weekly offs, and holidays.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2.5">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              PROCESS PIPELINE:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Punch Reconciliation:</strong> Consolidates biometric raw punches and manual punches to calculate First/Last swipe and working hours.
              </li>
              <li>
                <strong className="text-on-surface">Shift Alignment:</strong> Applies Shift Master in/out rules, grace minutes, and half-day deduction thresholds.
              </li>
              <li>
                <strong className="text-on-surface">Leave &amp; Holiday Integration:</strong> Maps approved leaves, company holidays, and weekly off rules to prevent false absenteeism.
              </li>
              <li>
                <strong className="text-on-surface">Comp-Off Generation:</strong> Automatically credits Comp-Off when working on designated holidays or weekly offs with minimum required hours.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
